// ==========================================
// 2. src/components/sections/VisionSection.js
// ==========================================
import React from 'react';
import { Rocket, Globe, Cpu, Users2, TrendingUp, Shield, Network, LucideComputer, BrainCircuit, Container } from 'lucide-react';
import SectionHeader from '../common/SectionHeader';

const VisionCard = ({ icon: Icon, title, description, colorClass }) => (
  <div className={`${colorClass} rounded-xl p-6 hover:shadow-xl transition-all duration-300`}>
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0">
        <Icon className="w-8 h-8 text-white" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
        <p className="text-white/90 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const VisionSection = () => {
  const visionPoints = [
    {
      icon: Globe,
      title: 'Global Logistics Network',
      description: 'Building the largest connected logistics network where every carrier, shipper, and partner can seamlessly collaborate.',
      colorClass: 'bg-gradient-to-br from-blue-600 to-blue-700'
    },
    {
      icon: Cpu,
      title: 'AI-Powered Automation',
      description: 'Advancing our AI to predict, prevent, and solve logistics challenges before they impact your operations.',
      colorClass: 'bg-gradient-to-br from-purple-600 to-purple-700'
    },
    {
      icon: Users2,
      title: 'Community-Driven Innovation',
      description: 'Creating a platform where operators share best practices and collectively improve the entire industry.',
      colorClass: 'bg-gradient-to-br from-green-600 to-green-700'
    },
    {
      icon: Shield,
      title: 'Zero-Touch Compliance',
      description: 'Achieving fully automated compliance across all regulatory frameworks—USPS, DOT, DOL, and beyond.',
      colorClass: 'bg-gradient-to-br from-orange-600 to-orange-700'
    }
  ];


  return (
    <section id="vision" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          title="Our Vision for the Future"
          subtitle="We're not just solving today's problems—we're building tomorrow's logistics infrastructure"
        />

        {/* Main Vision Statement */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white text-center shadow-xl">
            <Rocket className="w-16 h-16 mx-auto mb-4 text-white/90" />
            <h3 className="text-3xl font-bold mb-4">
              The Future is Autonomous Logistics
            </h3>
            <p className="text-xl leading-relaxed text-white/90">
              By 2030, we envision a world where logistics operations run themselves.
              Where AI handles dispatching, compliance is automatic, and humans focus
              on strategy and relationships—not spreadsheets and phone calls.
            </p>
          </div>
        </div>

        {/* Vision Points Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {visionPoints.map((point, index) => (
            <VisionCard key={index} {...point} />
          ))}
        </div>

        {/* Impact Metrics */}
        <div className="mt-16 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-2">Our Commitment to Impact</h3>
            <p className="text-gray-300">
              By 2027, we're committed to achieving:
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <BrainCircuit className="w-8 h-8 mx-auto mb-2 text-blue-400" />
              <div className="text-3xl font-bold mb-1">99%</div>
              <p className="text-sm text-gray-300">No Touch Logistics</p>
            </div>
            <div>
              <Network className="w-8 h-8 mx-auto mb-2 text-purple-400" />
              <div className="text-3xl font-bold mb-1">1,000+</div>
              <p className="text-sm text-gray-300">Integrations</p>
            </div>
            <div>
              <Container className="w-8 h-8 mx-auto mb-2 text-orange-400" />
              <div className="text-3xl font-bold mb-1">10+</div>
              <p className="text-sm text-gray-300">Load Boards</p>
            </div>
            <div>
              <Globe className="w-8 h-8 mx-auto mb-2 text-green-400" />
              <div className="text-3xl font-bold mb-1">Global Impact</div>
              <p className="text-sm text-gray-300">Land, Sea, Air</p>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default VisionSection;

